// Cell slug - the ONE frontend implementation of the cell URL slug rule.
//
// Mirrors backend/app/api/v1/public/slug.py::derive_cell_slug, the rule that was
// validated against the live batemo.com URLs and persisted in the backend's
// `product_listing.slug` (issue #18; until 2026-10-30 the `general_slug` column).
// The detail payload delivers it as `general.slug`. Every in-app link of the form /explorer?cell=<slug>&section=<s>
// and every slug -> cell lookup goes through this module; components must not
// carry their own slug regex.
//
// The override map is read from shared/cell-slugs.json, the fixture generated
// from slug.py (backend/scripts/generate_cell_slug_fixture.py). Only the named
// `overrides` export is imported so the bundle does not carry the cell list.
// frontend/tests/utils/cellSlug.test.js asserts this module against every
// triple in that fixture.

import { overrides as SLUG_OVERRIDES } from '../../../../shared/cell-slugs.json'

// Dropped outright (WordPress sanitize_title), must NOT become a separator.
const DROPPED_CHARS = /[()'‘’]/g
const SEPARATOR_RUN = /[^a-z0-9]+/g
const EDGE_HYPHENS = /^-+|-+$/g
// A stored slug is only trusted when it is a plain slug: call sites interpolate
// slugs into URLs and inline onclick handlers.
const PLAIN_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * slug.py::slugify - delete parentheses/apostrophes, trim, lowercase, replace
 * every remaining run of non-alphanumeric characters with '-', trim '-'.
 * @param {string} value
 * @returns {string}
 */
export function slugify(value) {
  if (!value) return ''
  return String(value)
    .replace(DROPPED_CHARS, '')
    .trim()
    .toLowerCase()
    .replace(SEPARATOR_RUN, '-')
    .replace(EDGE_HYPHENS, '')
}

/**
 * slug.py::derive_cell_slug - slugify(manufacturer) + '-' + slugify(name),
 * with the live-URL overrides applied last.
 * @param {string} manufacturer
 * @param {string} name
 * @returns {string}
 */
export function deriveCellSlug(manufacturer, name) {
  const derived = `${slugify(manufacturer)}-${slugify(name)}`.replace(EDGE_HYPHENS, '')
  return Object.prototype.hasOwnProperty.call(SLUG_OVERRIDES, derived)
    ? SLUG_OVERRIDES[derived]
    : derived
}

function storedSlug(cell) {
  const stored = cell.general_slug ?? cell.general?.slug
  return typeof stored === 'string' && PLAIN_SLUG.test(stored) ? stored : ''
}

/**
 * The canonical slug for a cell object: the persisted slug (backend
 * `product_listing.slug`) when the object carries one (categorised
 * `general.slug` from the detail payload, or a flat `general_slug`),
 * otherwise the slug.py rule on manufacturer + name.
 * @param {{manufacturer?: string, name?: string, general_slug?: string, general?: {slug?: string}}} cell
 * @returns {string}
 */
export function cellSlug(cell) {
  if (!cell) return ''
  return storedSlug(cell) || deriveCellSlug(cell.manufacturer, cell.name)
}

/**
 * LEGACY rule, kept ONLY as an alias so in-app links bookmarked before #18 keep
 * resolving (it deleted dots instead of hyphenating them: "Blade 2.0" gave
 * "byd-blade-20", now "byd-blade-2-0"). Never use it to generate a link.
 * @param {{manufacturer?: string, name?: string}} cell
 * @returns {string}
 */
export function legacyCellSlug(cell) {
  if (!cell) return ''
  return `${cell.manufacturer || ''}-${cell.name || ''}`
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Find the cell a ?cell= slug points at. Canonical slugs win; a legacy slug is
 * only tried when no cell carries the slug canonically, so an old alias can
 * never shadow another cell's current slug.
 * @template T
 * @param {T[]} cells
 * @param {string} slug
 * @returns {T|undefined}
 */
export function findCellBySlug(cells, slug) {
  if (!Array.isArray(cells) || !slug || typeof slug !== 'string') return undefined
  const wanted = slug.toLowerCase()
  return (
    cells.find(c => c && (cellSlug(c) === wanted || deriveCellSlug(c.manufacturer, c.name) === wanted)) ||
    cells.find(c => c && legacyCellSlug(c) === wanted)
  )
}
